/**
 * "Cần xử lý hôm nay" above the home hub's tabs (reference screen 01): the four piles that need a
 * person — dossiers to review, open work items, domains expiring within 30 days, registry
 * commands without a final result — each an exact number (one counts read) that opens the list
 * behind it. A tile the user may not read is left out; a count that cannot be loaded shows "—".
 * Below them, revenue per month for those who may read billing.
 */

import { CalendarClock, FolderOpen, ListTodo, ServerCrash } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { Counts } from '../../api/areas/counts';
import { StatCard } from '../../components/blocks';
import { useAccess } from '../../lib/access';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { hubUrl } from '../../nav/hubs';
import { HomeRevenue } from './HomeRevenue';

export function HomeToday() {
  const { t } = useTranslation('ux');
  const access = useAccess();
  const counts = useApi((signal) => (access.client ? Promise.resolve(null) : api.getCounts({ signal })), [access.client]);

  if (access.client) return null;

  const value = (pick: (c: Counts) => number | null | undefined): { text: string; n: number } => {
    if (counts.status === 'loading') return { text: '…', n: 0 };
    const n = counts.status === 'success' && counts.data ? pick(counts.data) : null;
    return n === null || n === undefined ? { text: '—', n: 0 } : { text: formatCount(n), n };
  };
  const tiles = [
    access.has(['dossier.review', 'compliance.read', 'work_item.read']) && {
      key: 'dossiers',
      icon: FolderOpen,
      v: value((c) => c.dossiers?.needs_review),
      to: hubUrl('home', 'dossiers', undefined, { stage: 'NEEDS_REVIEW' }),
    },
    // The queue opens on work items; operations kinds are behind its "Vận hành" switch.
    access.has(['work_item.read']) && { key: 'work', icon: ListTodo, v: value((c) => c.work_items && c.work_items.open - c.work_items.open_ops), to: hubUrl('home', 'queue') },
    access.has(['domain.read']) && {
      key: 'expiring',
      icon: CalendarClock,
      v: value((c) => c.domains?.expiring_within_30_days),
      to: hubUrl('domains', 'list', undefined, { lifecycle_state: 'ACTIVE', expires_within_days: '30' }),
    },
    access.has(['registry.transaction.read']) && {
      key: 'unresolved',
      icon: ServerCrash,
      v: value((c) => c.registry?.unresolved),
      to: hubUrl('ops', 'registry', 'transactions', { state: 'UNRESOLVED' }),
    },
  ].filter((x): x is Exclude<typeof x, false> => Boolean(x));

  return (
    <>
      {tiles.length ? (
        <section aria-label={t('today.title')} className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-home-today>
          {tiles.map((x) => (
            <StatCard
              key={x.key}
              icon={x.icon}
              label={t(`today.${x.key}.label` as 'today.dossiers.label')}
              value={x.v.text}
              tone={x.v.n > 0 ? (x.key === 'unresolved' ? 'serious' : 'warn') : 'neutral'}
              hint={x.v.n > 0 ? t(`today.${x.key}.hint` as 'today.dossiers.hint') : t('today.none')}
              to={x.to}
              linkLabel={t('today.open')}
            />
          ))}
        </section>
      ) : null}
      {access.has(['billing.read']) ? <HomeRevenue /> : null}
    </>
  );
}
